import { useState, useRef } from "react";
import { t, font } from "@realtyflows/ui";

/**
 * MoneyInput — formatted currency / percentage input.
 *
 * Props:
 *   value        — display string (controlled). For dollar: "1,250,000". For percent: "3.5"
 *   onChange      — (formattedValue, rawNumber) => void
 *   mode          — "dollar" | "percent"  (default "dollar")
 *   label         — optional label above input
 *   placeholder   — placeholder text
 *   hint          — hint text below
 *   hintVariant   — "default" | "success" | "warning" | "error"
 *   suffix        — override suffix (default: "USD" for dollar, "%" for percent)
 *   disabled      — disabled state
 *   error         — error message (overrides hint, shows red border)
 *   max           — max value (for percent mode, default 100)
 *   style         — container style override
 */
export default function MoneyInput({
  value = "",
  onChange,
  mode = "dollar",
  label,
  placeholder,
  hint,
  hintVariant = "default",
  suffix,
  disabled = false,
  error,
  max,
  style: containerStyle,
}) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const isDollar = mode === "dollar";
  const displaySuffix = suffix !== undefined ? suffix : (isDollar ? "USD" : "%");
  const displayPlaceholder = placeholder || (isDollar ? "0" : "0.00");

  const handleChange = (e) => {
    const el = e.target;

    if (isDollar) {
      // Strip everything except digits
      const raw = el.value.replace(/[^0-9]/g, "");
      if (!raw) { onChange?.("", 0); return; }
      const num = parseInt(raw, 10);
      const formatted = num.toLocaleString("en-US");

      // Calculate cursor position: count digits before cursor in old value,
      // then find same digit position in formatted value
      const cursorPos = el.selectionStart;
      const digitsBefore = (el.value.slice(0, cursorPos).replace(/[^0-9]/g, "")).length;
      let newCursor = 0;
      let digitCount = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/[0-9]/.test(formatted[i])) {
          digitCount++;
          if (digitCount === digitsBefore) { newCursor = i + 1; break; }
        }
      }
      if (digitsBefore === 0) newCursor = 0;

      onChange?.(formatted, num);

      // Restore cursor after React re-renders
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(newCursor, newCursor);
        }
      });
    } else {
      // Percent: allow digits and one decimal point, up to 2 decimal places
      let val = el.value.replace(/[^0-9.]/g, "");
      const parts = val.split(".");
      if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");
      if (parts[1]?.length > 2) val = parts[0] + "." + parts[1].slice(0, 2);
      const num = parseFloat(val);
      const limit = max ?? 100;
      if (val !== "" && !isNaN(num) && num > limit) return;
      onChange?.(val, num || 0);
    }
  };

  const handleFocus = () => {
    setFocused(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const hasError = !!error;
  const hintColor = hasError
    ? t.coral
    : hintVariant === "success"
    ? t.emerald
    : hintVariant === "warning"
    ? t.amber
    : t.textMuted;

  const borderColor = hasError
    ? t.coral
    : focused
    ? t.emerald
    : t.border;

  return (
    <div style={containerStyle}>
      {label && (
        <div style={{
          fontSize: 11, color: t.textSecondary, marginBottom: 4,
          fontWeight: 600, fontFamily: font.body,
        }}>
          {label}
        </div>
      )}

      <div style={{
        display: "flex", alignItems: "center",
        background: t.surfaceWarm,
        border: `1.5px solid ${borderColor}`,
        borderRadius: 6, overflow: "hidden",
        transition: "border-color 0.15s, box-shadow 0.15s",
        boxShadow: focused ? `0 0 0 3px ${hasError ? `${t.coral}18` : `${t.emerald}18`}` : "none",
        opacity: disabled ? 0.6 : 1,
      }}>
        {isDollar && (
          <span style={{
            padding: "0 10px", fontSize: 13, fontWeight: 600,
            color: t.textSecondary, background: t.surface,
            borderRight: `1px solid ${t.border}`,
            height: 36, display: "flex", alignItems: "center", flexShrink: 0,
            fontFamily: font.mono,
          }}>
            $
          </span>
        )}

        <input
          ref={inputRef}
          type="text"
          inputMode={isDollar ? "numeric" : "decimal"}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={displayPlaceholder}
          disabled={disabled}
          style={{
            flex: 1, height: 36, padding: isDollar ? "0 10px" : "0 10px 0 12px",
            fontSize: 13, fontWeight: 500, color: t.textPrimary,
            background: "transparent", border: "none", outline: "none",
            fontFamily: font.body, minWidth: 0,
          }}
        />

        {displaySuffix && (
          <span style={{
            padding: "0 10px", fontSize: 11, fontWeight: 500,
            color: t.textMuted, flexShrink: 0, fontFamily: font.mono,
          }}>
            {displaySuffix}
          </span>
        )}
      </div>

      {(hint || error) && (
        <div style={{
          fontSize: 10, color: hintColor, paddingLeft: 2, marginTop: 3,
          fontFamily: font.body, lineHeight: 1.4,
        }}>
          {error || hint}
        </div>
      )}
    </div>
  );
}
